import type { ReactNode } from 'react';
import './ibm-products-flags';
import { Button } from '@carbon/react';
import { PageHeader } from 'components/page-header';
import { SuccessToast } from 'components/success-toast';
import ErrorToast from 'components/error-toast';
import styles from './pricing-page-layout.module.scss';

export interface PricingToast {
  type: 'success' | 'error';
  message: string;
  subtitle?: string;
}

interface PricingPageLayoutProps {
  nav: ReactNode;
  pageTitle: string;
  pageActions?: ReactNode; // record-level actions (Duplicate / Delete) — PageHeader
  contentEyebrow?: string;
  contentTitle?: ReactNode;
  onSave?: () => void;
  canSave?: boolean;
  isSaving?: boolean;
  saveLabel?: string;
  saveStatus?: ReactNode; // dirty/saved indicator shown left of Save
  toast?: PricingToast | null;
  onToastClose?: () => void;
  children: ReactNode;
}

// Master-detail frame for the pricing pages, mirroring the app's scenario-form layout:
// a PageHeader (title + record actions), the left-rail nav, and a content-header bar
// carrying Save above the form body.
export function PricingPageLayout({
  nav,
  pageTitle,
  pageActions,
  contentEyebrow,
  contentTitle,
  onSave,
  canSave = false,
  isSaving = false,
  saveLabel = 'Save changes',
  saveStatus,
  toast,
  onToastClose,
  children,
}: PricingPageLayoutProps) {
  const handleClose = () => {
    onToastClose?.();
    return true;
  };
  return (
    <div className={styles.page}>
      <PageHeader
        breadcrumbs={[]}
        title={pageTitle}
        actions={pageActions ?? null}
      />
      <div className={styles.layout}>
        {nav}
        <div className={styles.main}>
          {(contentTitle || onSave) && (
            <div className={styles.contentHeader}>
              <div>
                {contentEyebrow && (
                  <div className={styles.eyebrow}>{contentEyebrow}</div>
                )}
                {contentTitle && (
                  <div className={styles.title}>{contentTitle}</div>
                )}
              </div>
              {onSave && (
                <div className={styles.actions}>
                  {saveStatus && (
                    <span className={styles.saveStatus}>{saveStatus}</span>
                  )}
                  <Button
                    kind="primary"
                    disabled={!canSave || isSaving}
                    onClick={onSave}
                  >
                    {isSaving ? 'Saving…' : saveLabel}
                  </Button>
                </div>
              )}
            </div>
          )}
          <div className={styles.body}>{children}</div>
        </div>
      </div>
      {toast?.type === 'success' && (
        <SuccessToast
          message={toast.message}
          subtitle={toast.subtitle}
          onClose={handleClose}
        />
      )}
      {toast?.type === 'error' && (
        <ErrorToast
          message={toast.message}
          subtitle={toast.subtitle}
          onClose={handleClose}
        />
      )}
    </div>
  );
}
